import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { getProduct } from '../services/productsService'
import { addToHistory } from '../services/scanHistory'
import { getDisabledRetailers } from '../services/retailerPrefs'
import { getSavedLocation, saveLocation, requestLocation } from '../services/geo'
import { prettyChain } from '../lib/chains'
import { cleanProductName } from '../lib/text'

// "4,99€" — comma decimal, no space, matching the Croatian retail convention.
function formatEur(num) {
  // Non-breaking space before € so the amount and symbol never wrap apart.
  return Number.isNaN(num) ? '—' : `${num.toFixed(2).replace('.', ',')} €`
}

// "2026-05-29" -> "29.05.2026."
function formatDate(value) {
  if (!value) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return m ? `${m[3]}.${m[2]}.${m[1]}.` : value
}

// Is the price date today (local time)? Today's prices don't need a date label.
function isToday(value) {
  if (typeof value !== 'string') return false
  const d = new Date()
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
  return value.slice(0, 10) === iso
}

const minPrice = (chain) => {
  const n = Number.parseFloat(chain.min_price)
  return Number.isNaN(n) ? Infinity : n
}

// Shared price-age sub-label (reused by the winner card and the rest list).
// Staleness is decided by the backend: a price is stale only
// when its chain hasn't been successfully crawled recently — NOT when the price
// itself simply hasn't changed. The FE only formats: it renders the BE `stale`
// flag as a warning, or — for a fresh price not set today — the "valid from" date.
function PriceAgeLabel({ stale, priceDate }) {
  const { t } = useTranslation()
  if (stale) {
    return <p className="text-xs text-warn-fg font-medium">{t('results.ageStale')}</p>
  }
  if (priceDate && !isToday(priceDate)) {
    return <p className="text-xs text-faint">{t('results.ageValidFrom', { date: formatDate(priceDate) })}</p>
  }
  return null
}

// "Akcija" pill — shown when the cheapest store's price is below its anchor
// (backend `on_promo`). Soft-green to read as a positive discount, distinct from
// the solid-green "cheapest" pill.
function PromoBadge({ onPromo }) {
  const { t } = useTranslation()
  if (!onPromo) return null
  return (
    <span className="bg-success-surface text-success-fg text-xs font-semibold px-2 py-0.5 rounded-pill">
      {t('results.promo')}
    </span>
  )
}

// Compact per-price meta: a single "directions" link to the store you'd actually
// go to — the closest store at this price (for a national-only chain, DM with
// `located` false, the nearest branch, plus a "chain-wide price" note).
// Deliberately terse: no city name and no store-count, to keep the row uncluttered.
function StoreMeta({ chain }) {
  const { t } = useTranslation()
  const store = chain.cheapest_store
  const hasCoords = store && store.lat != null && store.lon != null
  const chainWide = chain.located === false
  if (!chainWide && !hasCoords) return null

  const dirUrl = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lon}`
    : null

  return (
    <div className="text-xs text-faint space-y-0.5">
      {chainWide && <p>{t('results.chainWide')}</p>}
      {dirUrl && (
        <a
          href={dirUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-primary-text hover:underline"
        >
          📍 {t('results.directions')}
        </a>
      )}
    </div>
  )
}

// Inner view is keyed by `ean` (see ResultsPage) so it remounts — and resets to
// the "loading" state — whenever a different barcode is looked up.
function ResultsView({ ean }) {
  const { t } = useTranslation()
  const [status, setStatus] = useState('loading') // loading | notFound | error | loaded
  const [product, setProduct] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const [loadingTimeout, setLoadingTimeout] = useState(false)
  const [shareToast, setShareToast] = useState(false)
  const [location, setLocation] = useState(getSavedLocation()) // { lat, lon } | null (geolocation)
  const [geoError, setGeoError] = useState(false)
  // True when the geolocation scope had no prices, so we fell back to national.
  const [locatedFellBack, setLocatedFellBack] = useState(false)

  useEffect(() => {
    let active = true
    setStatus('loading')
    setLocatedFellBack(false)

    // GPS-only scope. The backend excludes the user's hidden retailers, decides
    // the winner, and falls back to national itself — so the client just passes
    // the scope + exclusions and renders whatever comes back.
    const scope = location ? { lat: location.lat, lon: location.lon } : {}
    const exclude = [...getDisabledRetailers()]

    async function load() {
      try {
        const data = await getProduct(ean, { ...scope, exclude })
        if (!active) return
        setProduct(data)
        setLocatedFellBack(Boolean(data.located_fell_back))
        setStatus('loaded')
        addToHistory(data, data.winner_price ?? 0) // record only successful lookups
      } catch (err) {
        if (!active) return
        setStatus(err.response?.status === 404 ? 'notFound' : 'error')
      }
    }
    load()

    return () => {
      active = false
    }
  }, [ean, retryCount, location])

  // Ask the browser for the user's location (the "near you" path).
  async function handleUseLocation() {
    setGeoError(false)
    try {
      const loc = await requestLocation()
      saveLocation(loc)
      setLocation(loc) // triggers a radius-scoped re-fetch
    } catch {
      setGeoError(true) // declined / unavailable → show an error; GPS is optional
    }
  }

  // Drop the geolocation scope (back to national prices).
  function handleClearLocation() {
    saveLocation(null)
    setLocation(null)
  }

  // 8-second API timeout
  useEffect(() => {
    if (status !== 'loading') return
    const timer = setTimeout(() => setLoadingTimeout(true), 8000)
    return () => clearTimeout(timer)
  }, [status])

  // The backend already excluded the user's hidden retailers, sorted cheapest
  // first, and decided the winner (is_winner) + savings — so the frontend does
  // no ranking, only grouping and rendering (kept consistent across clients).
  const chains = product?.chains || []
  const unverified = product?.unverified_prices || []
  const winnerChains = chains.filter((c) => c.is_winner)
  const restChains = chains.filter((c) => !c.is_winner)
  // Non-winning verified chains + the inline unverified ones (Lidl), cheapest-first.
  const rest = [...restChains, ...unverified].sort((a, b) => minPrice(a) - minPrice(b))
  const winnerPrice = product?.winner_price ?? 0
  const maxSaving = product?.max_saving ?? 0
  const singleStore = Boolean(product?.single_store)
  // Product exists (HTTP 200) but every retailer is hidden by the user's settings.
  const allHidden = Boolean(product) && chains.length === 0 && unverified.length === 0

  // Share the comparison (URL already encodes the EAN). Native sheet first,
  // clipboard + toast as the fallback.
  async function handleShare() {
    if (!product) return
    const winner = winnerChains[0]
    const text = winner
      ? t('results.shareText', {
          name: cleanProductName(product.name),
          price: formatEur(winnerPrice),
          chain: prettyChain(winner.chain),
        })
      : cleanProductName(product.name)
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, text, url })
      } catch {
        /* user dismissed the share sheet */
      }
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <main id="main-content" className="max-w-2xl mx-auto px-4 py-8">
      <Link to="/" className="min-h-[48px] inline-flex items-center px-2 mb-6 text-primary-text hover:underline">
        ← {t('results.rescan')}
      </Link>

      {status === 'loading' && !loadingTimeout && (
        <div className="space-y-4 animate-pulse" aria-label={t('results.loadingAria')}>
          <p className="text-base text-muted">{t('results.searchingPrices')}</p>
          {/* Winner card skeleton */}
          <div className="rounded-card bg-surface-2 p-4">
            <div className="h-4 bg-surface-2-hover rounded w-24 mb-3" />
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded bg-surface-2-hover" />
              <div className="flex-1 h-5 bg-surface-2-hover rounded w-32" />
              <div className="h-8 w-20 bg-surface-2-hover rounded" />
            </div>
          </div>
          {/* Result row skeletons */}
          <div className="h-[60px] bg-surface-2 rounded-card" />
          <div className="h-[60px] bg-surface-2 rounded-card" />
          <div className="h-[60px] bg-surface-2 rounded-card" />
        </div>
      )}

      {status === 'loading' && loadingTimeout && (
        <div role="alert" className="bg-surface rounded-card shadow-card p-6">
          <h1 className="text-xl font-semibold text-fg">{t('results.slowTitle')}</h1>
          <p className="mt-2 text-muted">{t('results.slowBody')}</p>
          <button onClick={() => window.location.reload()} className="mt-4 w-full bg-primary text-on-primary rounded-btn px-4 py-3.5 text-base font-semibold hover:bg-primary-hover">
            {t('results.retry')}
          </button>
        </div>
      )}

      {status === 'notFound' && (
        <div role="alert" className="bg-surface rounded-card shadow-card p-6">
          <h1 className="text-xl font-semibold text-fg">{t('results.notFoundTitle')}</h1>
          <p className="mt-2 text-fg">
            {t('results.notFoundBody')}
          </p>
          <p className="mt-2 text-muted">
            {t('results.notFoundHint')}
          </p>
          <p className="mt-4 text-sm text-muted">
            {t('results.scannedBarcode')} <span className="text-fg font-medium">{ean}</span>
          </p>
          <Link to="/" className="mt-4 block w-full text-center bg-primary text-on-primary rounded-btn px-4 py-3.5 text-base font-semibold hover:bg-primary-hover">
            {t('results.scanAnother')}
          </Link>
        </div>
      )}

      {status === 'error' && (
        <div role="alert" className="bg-surface rounded-card shadow-card p-6">
          <h1 className="text-xl font-semibold text-fg">{t('results.errorTitle')}</h1>
          <p className="mt-2 text-muted">
            {t('results.errorBody')}
          </p>
          <button
            onClick={() => { setStatus('loading'); setRetryCount(c => c + 1); setLoadingTimeout(false) }}
            className="mt-4 w-full bg-primary text-on-primary rounded-btn px-4 py-3.5 text-base font-semibold hover:bg-primary-hover"
          >
            {t('results.retry')}
          </button>
        </div>
      )}

      {status === 'loaded' && product && (
        <>
          <header className="mb-6 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-fg">{cleanProductName(product.name)}</h1>
              <p className="mt-1 text-muted">
                {[product.brand, product.quantity]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleShare}
              aria-label={t('results.shareAria')}
              className="shrink-0 mt-1 h-11 w-11 flex items-center justify-center rounded-full text-primary hover:bg-surface-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </header>

          {/* Location control — "cheapest near you" via browser geolocation
              (radius search). GPS only; no city picker. */}
          <div className="mb-4" data-testid="location-bar">
            {location ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-fg font-medium">📍 {t('results.nearYou')}</span>
                <button
                  type="button"
                  data-testid="clear-location"
                  onClick={handleClearLocation}
                  className="text-primary hover:underline"
                >
                  {t('results.clearLocation')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="use-location"
                onClick={handleUseLocation}
                className="inline-flex items-center gap-1 rounded-btn bg-surface-2 px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2-hover"
              >
                📍 {t('results.useLocation')}
              </button>
            )}
          </div>

          {geoError && (
            <p data-testid="geo-error" className="mb-3 text-sm text-warn-fg">{t('results.geoError')}</p>
          )}

          {locatedFellBack && (
            <p data-testid="location-fellback" className="mb-3 text-sm text-warn-fg">
              {t('results.nearYouFellBack')}
            </p>
          )}

          {shareToast && (
            <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-fg text-surface text-sm rounded-pill px-4 py-2 shadow-modal">
              {t('results.copied')}
            </div>
          )}

          {winnerChains.length > 0 && (
            <div
              data-testid="winner-card"
              className="bg-success-surface border-2 border-success rounded-card p-4 mb-4"
            >
              {winnerChains.length === 1 ? (
                <>
                  <div className="bg-success text-on-success text-sm font-semibold px-2 py-0.5 rounded-pill inline-block mb-3">
                    {t(singleStore ? 'results.onlyPrice' : 'results.cheapest')}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-surface flex items-center justify-center">
                      <img
                        src={`/retailers/${winnerChains[0].chain}.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-bold text-fg">{prettyChain(winnerChains[0].chain)}</p>
                      <PriceAgeLabel stale={winnerChains[0].stale} priceDate={winnerChains[0].price_changed_at} />
                      <StoreMeta chain={winnerChains[0]} />
                    </div>
                    <div className="shrink-0 text-right">
                      {winnerChains[0].on_promo && (
                        <div className="mb-1 flex justify-end">
                          <PromoBadge onPromo />
                        </div>
                      )}
                      <p data-testid="winner-price" className="text-3xl font-bold text-fg">{formatEur(winnerPrice)}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-success text-on-success text-sm font-semibold px-2 py-0.5 rounded-pill inline-block">
                      {t('results.cheapest')}
                    </span>
                    <span data-testid="winner-price" className="text-3xl font-bold text-fg">{formatEur(winnerPrice)}</span>
                  </div>
                  <p className="text-sm text-muted mb-2">
                    {t('results.storesSamePrice', { count: winnerChains.length })}
                  </p>
                  <ul className="flex flex-wrap gap-2">
                    {winnerChains.map((chain) => (
                      <li
                        key={chain.chain}
                        className="flex items-center gap-2 bg-surface-2 rounded-pill py-1 pl-1 pr-3"
                      >
                        <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-surface flex items-center justify-center">
                          <img
                            src={`/retailers/${chain.chain}.jpg`}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-fg">{prettyChain(chain.chain)}</span>
                      </li>
                    ))}
                  </ul>
                  {winnerChains.some((c) => c.stale) && (
                    <p className="text-xs text-warn-fg font-medium mt-2">{t('results.someStale')}</p>
                  )}
                </>
              )}
            </div>
          )}

          {restChains.length > 0 && maxSaving >= 0.01 && (
            <p className="text-sm text-muted mb-3">
              <Trans
                i18nKey="results.saveUpTo"
                values={{ amount: formatEur(maxSaving) }}
                components={{ b: <span className="font-semibold text-savings-fg" /> }}
              />
            </p>
          )}

          {rest.length ? (
            <ul className="space-y-2">
              {rest.map((chain) => {
                const price = minPrice(chain)
                const isUnverified = !chain.verified
                const showDelta = chain.delta != null // backend-set; null for winner/unverified
                const deltaText = chain.delta_pct === 0
                  ? t('results.moreExpensive', { amount: formatEur(chain.delta) })
                  : t('results.moreExpensivePct', { amount: formatEur(chain.delta), pct: chain.delta_pct })
                return (
                  <li
                    key={`${chain.chain}-${chain.code}`}
                    data-testid={isUnverified ? 'unverified-row' : undefined}
                    className={`flex items-center rounded-card border p-4 ${isUnverified ? 'border-warn-line bg-warn-surface' : 'border-line bg-surface'}`}
                  >
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-surface flex items-center justify-center">
                      <img
                        src={`/retailers/${chain.chain}.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="font-semibold text-fg">{prettyChain(chain.chain)}</p>
                      <PriceAgeLabel stale={chain.stale} priceDate={chain.price_changed_at} />
                      <StoreMeta chain={chain} />
                      {isUnverified && (
                        <p className="mt-1 text-xs font-medium text-warn-fg">⚠ {t('results.shelfWarning')}</p>
                      )}
                    </div>
                    <div className="ml-3 shrink-0 text-right">
                      {chain.on_promo && <div className="mb-1 flex justify-end"><PromoBadge onPromo /></div>}
                      <p data-testid="list-price" className={`text-xl font-bold ${isUnverified ? 'text-muted' : 'text-fg'}`}>{formatEur(price)}</p>
                      {/* delta comes from the backend — null for the winner and for
                          unverified rows (which can be cheaper than the winner). */}
                      {showDelta && <p className="text-base text-muted">{deltaText}</p>}
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : allHidden ? (
            <div className="bg-surface rounded-card shadow-card p-6 text-center">
              <p className="text-muted">{t('results.allHidden')}</p>
              <Link to="/settings" className="mt-4 inline-block bg-primary text-on-primary rounded-btn px-6 py-3.5 text-base font-semibold hover:bg-primary-hover">
                {t('results.enableAll')}
              </Link>
            </div>
          ) : winnerChains.length === 0 ? (
            <p className="text-muted">{t('results.noPrices')}</p>
          ) : winnerChains.length === 1 ? (
            <p className="text-sm text-muted mt-2">{t('results.onlyOneStore')}</p>
          ) : null}

          <p className="mt-6 text-center text-xs text-faint whitespace-pre-line">{t('results.lowestReported')}</p>

          <p className="mt-2 text-center text-xs text-faint">
            EAN: <span className="font-mono select-all">{product.ean}</span>
          </p>
        </>
      )}
    </main>
  )
}

export default function ResultsPage() {
  const { ean } = useParams()
  return <ResultsView key={ean} ean={ean} />
}
